import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'expo-router';
import { useSetAtom } from 'jotai';
import { SquarePen } from 'lucide-react-native';

import { Button, ButtonIcon, ButtonText } from '@/components/ui/button';
import type { AskConversationSnapshot } from '@/services/ask/api';
import { askKeys } from '@/services/ask/queryOptions';
import { referenceMessageBodyAtom } from '@/services/atoms/inquiry';
import { getCancerTypesV2QueryOptions } from '@/services/getCancerTypesV2';
import {
  type InquiryCancerTypePrefill,
  resolveInquiryCancerTypePrefill,
  setInquiryCancerTypePrefill,
} from '@/services/stores/inquiryCancerTypePrefillStore';
import { trackLibraryChatAskExpertTriggered, type AskExpertSurface } from '@/util/analyticsEvents';
import { getStackedInquiryHref } from '@/util/stackedInquiryHref';

type AskExpertHandoff = {
  conversationId: string;
  turnId: string;
  questionText: string;
  surface: AskExpertSurface;
};

/**
 * Takes a turn to an expert: the question is carried into the General Inquiry
 * composer through the in-memory prefill it already reads, so the text never
 * lands in a URL.
 *
 * The composer opens over the chat rather than in the Inquiry tab, so the
 * reader can return to the conversation.
 *
 * The conversation's cancer type goes with it, read from the cached snapshot
 * at the moment of the tap: the server re-derives it every turn, so a value
 * captured when the conversation opened can already be wrong.
 */
export function useAskExpertHandoff() {
  const router = useRouter();

  const queryClient = useQueryClient();

  const setReferenceMessageBody = useSetAtom(referenceMessageBodyAtom);

  /**
   * Reports the cancer type the inquiry form will show, not the raw one the
   * server sent: an id the form does not offer becomes a hint there, and the
   * event must say so too or it counts prefills nobody saw. Resolves against
   * the same cancer-type query the form waits on, so the two cannot disagree;
   * if that list cannot load, the form prefills nothing and neither does the
   * event. Sent without holding up navigation.
   */
  const trackAskExpertTriggered = async (
    properties: { conversation_id: string; turn_id: string; surface: AskExpertSurface },
    prefill: InquiryCancerTypePrefill | null
  ) => {
    const cancerTypes = prefill
      ? await queryClient.ensureQueryData(getCancerTypesV2QueryOptions()).catch(() => undefined)
      : undefined;

    const resolved = resolveInquiryCancerTypePrefill(prefill, cancerTypes?.cancerTypeLookup);

    trackLibraryChatAskExpertTriggered({
      ...properties,
      prefilled_cancer_type_id: resolved.cancerFocusId ? Number(resolved.cancerFocusId) : null,
      has_cancer_type_hint: !!resolved.hint,
    });
  };

  return ({ conversationId, turnId, questionText, surface }: AskExpertHandoff) => {
    const primaryCancerType = queryClient.getQueryData<AskConversationSnapshot>(
      askKeys.snapshot(conversationId)
    )?.primaryCancerType;

    const prefill =
      primaryCancerType && (primaryCancerType.id || primaryCancerType.label)
        ? { conversationId, id: primaryCancerType.id, label: primaryCancerType.label }
        : null;

    trackAskExpertTriggered(
      { conversation_id: conversationId, turn_id: turnId, surface },
      prefill
    ).catch(() => undefined);

    setReferenceMessageBody(questionText);

    setInquiryCancerTypePrefill(prefill);

    router.push(getStackedInquiryHref());
  };
}

/**
 * The way to an expert instead, as a button. Shared by the thread (on a turn
 * that matched nothing) and the Summary panel, so it takes its question and
 * ids as props rather than an `AskTurn` — the Summary panel only has
 * `AskTurnContext`, which has neither.
 */
export function AskExpertButton({
  onHandoff,
  ...handoff
}: AskExpertHandoff & { surface: 'thread' | 'summary'; onHandoff?: () => void }) {
  const handOff = useAskExpertHandoff();

  const handlePress = () => {
    handOff(handoff);

    onHandoff?.();
  };

  return (
    <Button
      size="sm"
      variant="outline"
      action="secondary"
      className="self-start"
      onPress={handlePress}
      accessibilityHint="Opens a new inquiry with this question filled in">
      <ButtonIcon as={SquarePen} className="text-typography-700" />
      <ButtonText className="text-typography-700">Ask an Expert</ButtonText>
    </Button>
  );
}
